/**
 * PlayerImpactBus.ts — collects collision events from the broad-phase
 * resolver, filters them down to player-boat-only contacts, applies
 * a per-kind cooldown, and exposes a drain API for UI / audio.
 *
 * Collision resolution can re-fire across consecutive frames as the
 * boat slides along an obstacle, which would otherwise produce a
 * "thud" every single frame of a scrape. We coalesce same-kind hits
 * within a 250 ms window into a single event.
 *
 * AI / moored boats banging the dock are intentionally dropped — they
 * would produce off-screen sounds with no visual cause.
 */
import type { Boat } from "./Boat";
import type { ImpactEvent } from "./Collisions";

/**
 * Player-boat impact event surfaced to UI / audio. Decoupled from the
 * internal {@link ImpactEvent} (which references the simulation `Boat`).
 */
export interface BoatImpactEvent {
  kind:
    | "dock"
    | "ground"
    | "boat"
    | "buoy"
    | "embankment"
    | "powerline"
    | "bridge"
    | "obstacle";
  /** Closing speed at the contact, m/s. */
  speed: number;
}

/** Same-kind events within this window collapse to a single event. */
const COOLDOWN_MS = 250;

/** Hard cap so an idle consumer cannot cause unbounded growth. */
const MAX_QUEUED = 32;

export class PlayerImpactBus {
  private readonly pending: BoatImpactEvent[] = [];
  private readonly lastTimeMs: Partial<Record<ImpactEvent["kind"], number>> =
    {};

  constructor(private readonly playerBoat: Boat) {}

  /**
   * Ingest one frame's worth of raw collision events from the broad
   * phase. Non-player events are dropped, same-kind cooldown is
   * applied, and the surviving events are queued for later drain.
   */
  ingestFrame(frameImpacts: readonly ImpactEvent[]): void {
    if (frameImpacts.length === 0) return;
    const now = performance.now();
    for (const ev of frameImpacts) {
      if (ev.boat !== this.playerBoat) continue;
      const last = this.lastTimeMs[ev.kind] ?? 0;
      if (now - last < COOLDOWN_MS) continue;
      this.lastTimeMs[ev.kind] = now;
      this.pending.push({ kind: ev.kind, speed: ev.speed });
      if (this.pending.length > MAX_QUEUED) this.pending.shift();
    }
  }

  /**
   * Drain queued events. Returns an independent array; subsequent
   * calls return empty until new events come in.
   */
  consume(): BoatImpactEvent[] {
    if (this.pending.length === 0) return [];
    const out = this.pending.slice();
    this.pending.length = 0;
    return out;
  }
}
